import './styles/SelectCheckbox.scss';

import { ArrowCounterClockwise, CaretDown } from '@phosphor-icons/react';
import { Fragment, memo, useEffect, useMemo, useState } from 'react';

import { cn } from '../../../../helpers/classBuilders';
import { useClassHelper, useUpdateEffect } from '../../../../hooks';
import { IdLabelType } from '../../../../types';
import { Checkbox } from '../../../Checkbox';
import { IconRenderer } from '../../../IconRenderer';
import { Popover } from '../../../Popover';
import { Paragraph } from '../../../Typography';

const sizes = {
  sm: 'sm',
  md: 'md',
  lg: 'lg',
  auto: 'auto',
} as const;

const Variants = {
  default: 'default',
  reset: 'reset',
} as const;

export interface SelectCheckboxProps {
  options: IdLabelType[];
  label: string;
  id: string;
  contentSize?: keyof typeof sizes;
  selectAll?: boolean;
  withScroller?: boolean;
  defaultSelectedOptions?: string[];
  onSubmit: (checkboxId: string, valuesId: string[]) => void;
  disabled?: boolean;
  loading?: boolean;
  withActions?: boolean;
  /**
   * 'reset': no Reset/Apply footer — every toggle submits immediately and a
   * divider plus a single "reset to default" action row is rendered instead.
   */
  variant?: keyof typeof Variants;
  /** Label of the reset action row (variant 'reset' only). */
  resetLabel?: string;
  /** Fired by the reset action row (variant 'reset' only). */
  onReset?: () => void;
  dataCy?: string;
  fullWidth?: boolean;
}

const componentName = 'c-SelectCheckbox';

const RawSelectCheckbox = ({
  options,
  label,
  id,
  contentSize = 'auto',
  selectAll = true,
  withScroller = true,
  defaultSelectedOptions,
  disabled,
  onSubmit,
  loading,
  withActions = true,
  variant = Variants.default,
  resetLabel = 'Reset to default',
  onReset,
  dataCy = '',
  fullWidth = false,
}: SelectCheckboxProps) => {
  const isResetVariant = variant === Variants.reset;
  const [state, setState] = useState(() => {
    return {
      popoverIsOpen: false,
      ...(defaultSelectedOptions?.length > 0
        ? {
            selectedOptions: new Set(defaultSelectedOptions),
          }
        : {
            selectedOptions: new Set(),
          }),
      tempSelectedOptions: new Set(),
      isSelectAllChecked: false,
    };
  });

  useUpdateEffect(() => {
    // Update initial defaultSelectedOptions
    if (defaultSelectedOptions) {
      if (isResetVariant) {
        // Instant-apply: the parent re-renders while the popover is still
        // open, so keep the visible (temp) selection in sync instead of
        // clearing it and don't touch the popover-open flag.
        setState(prev => {
          return {
            ...prev,
            selectedOptions: new Set(defaultSelectedOptions),
            tempSelectedOptions: new Set(defaultSelectedOptions),
            isSelectAllChecked: false,
          };
        });
        return;
      }
      setState({
        popoverIsOpen: false,
        selectedOptions: new Set(defaultSelectedOptions),
        tempSelectedOptions: new Set(),
        isSelectAllChecked: false,
      });
    }
  }, [defaultSelectedOptions]);

  const c = useClassHelper(componentName);

  const selectedSize = state.selectedOptions.size;

  const cnListSizes = useMemo(() => {
    const size = sizes[contentSize] ?? 'auto';

    return `__list--${size}`;
  }, [contentSize]);

  useEffect(() => {
    const hasPrev = state.selectedOptions.size > 0;

    if (hasPrev && state.popoverIsOpen) {
      setState(prev => {
        return {
          ...prev,
          popoverIsOpen: false,
          tempSelectedOptions: new Set(prev.selectedOptions),
        };
      });
    }
  }, [state.popoverIsOpen]);

  const isSelectAllChecked = useMemo(() => {
    return (
      options.length > 0 &&
      options.every(option => state.tempSelectedOptions.has(option.id))
    );
  }, [options, state.tempSelectedOptions]);

  // Instant-apply path (variant 'reset'): the temp and applied selections
  // move together and every change submits straight away. The onSubmit call
  // is deferred to the next event loop cycle so the parent doesn't update
  // while this component is rendering.
  const applyInstantSelection = (newSelectedOptions: Set<string>) => {
    setState(prev => {
      return {
        ...prev,
        selectedOptions: new Set(newSelectedOptions),
        tempSelectedOptions: newSelectedOptions,
      };
    });
    setTimeout(() => {
      onSubmit(id, [...newSelectedOptions] as string[]);
    }, 0);
  };

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    if (isResetVariant) {
      applyInstantSelection(
        isChecked ? new Set(options.map(option => option.id)) : new Set(),
      );
      return;
    }
    if (isChecked) {
      setState(prev => {
        const allOptionIds = new Set(options.map(option => option.id));
        return {
          ...prev,
          tempSelectedOptions: allOptionIds,
          isSelectAllChecked: true,
        };
      });
    } else {
      setState(prev => {
        return {
          ...prev,
          tempSelectedOptions: new Set(),
          isSelectAllChecked: false,
        };
      });
    }
  };

  const handleOptionChange = (optionId: string, checked: boolean) => {
    if (isResetVariant) {
      const newSelectedOptions = new Set(state.tempSelectedOptions);
      if (checked) {
        newSelectedOptions.add(optionId);
      } else {
        newSelectedOptions.delete(optionId);
      }
      applyInstantSelection(newSelectedOptions as Set<string>);
      return;
    }
    setState(prev => {
      const newSelectedOptions = new Set(prev.tempSelectedOptions);
      if (checked) {
        newSelectedOptions.add(optionId);
      } else {
        newSelectedOptions.delete(optionId);
      }
      return { ...prev, tempSelectedOptions: newSelectedOptions };
    });
  };

  const handleResetToDefault = () => {
    onReset?.();
  };

  const handleReset = () => {
    setState(prev => {
      const newState = {
        ...prev,
        tempSelectedOptions: new Set(),
      };

      setTimeout(() => {
        onSubmit(id, []);
      }, 0);

      return newState;
    });
  };

  const handleApply = (closePopover?: () => void) => {
    setState(prev => {
      const newState = {
        ...prev,
        popoverIsOpen: false,
        tempSelectedOptions: new Set(),
        selectedOptions: new Set(prev.tempSelectedOptions),
      };

      // Using setTimeout to defer the onSubmit call to the next event loop cycle,
      // preventing state updates in the parent component during the current render cycle.
      // This avoids the "Cannot update a component while rendering a different component" warning.
      setTimeout(() => {
        onSubmit(id, [...newState.selectedOptions] as string[]);
      }, 0);

      return newState;
    });

    if (closePopover) {
      closePopover();
    }
  };

  return (
    <Popover
      classNames={
        fullWidth
          ? {
              wrapper: 'w-full',
              trigger: 'w-full',
            }
          : undefined
      }
      disabled={disabled || loading}
      onClose={() => {
        if (!withActions && !isResetVariant) {
          // automatically update on close
          handleApply();
        }
        setState(prev => {
          return {
            ...prev,
            popoverIsOpen: false,
            tempSelectedOptions: new Set(),
          };
        });
      }}
      onOpen={() => {
        setState(prev => {
          return {
            ...prev,
            popoverIsOpen: true,
          };
        });
      }}
      id={id}
      trigger={({ isOpen }) => {
        return (
          <span
            className={`${c(
              '__button',
              isOpen || selectedSize ? '__button--open' : '',
              !isOpen && selectedSize ? '__button--selected' : '',
              disabled ? '__disabled' : '',
            )} ${cn('border border-gray-200 justify-between', {
              'w-full': fullWidth,
            })}`}
            aria-disabled={disabled ? 'true' : 'false'}
            data-cy={dataCy ? `${dataCy}-trigger` : undefined}
          >
            <Paragraph className={c('__button-label-wrapper')}>
              <span className={c('__button-label')}>{label}</span>
              {selectedSize > 0 && (
                <span className={c('__button-counter')}>({selectedSize})</span>
              )}
            </Paragraph>
            {loading ? (
              <IconRenderer variant="spinner-light" />
            ) : (
              <CaretDown
                className={c(
                  '__button-caret',
                  isOpen ? '__button-caret--open' : '',
                )}
              />
            )}
          </span>
        );
      }}
      content={({ handleClose }) => {
        return (
          <div
            className={c('__content', isResetVariant ? '__content--reset' : '')}
          >
            <ul
              className={c(
                '__list',
                cnListSizes,
                withScroller ? '__list--with-scroller' : '',
              )}
            >
              {selectAll && (
                <li>
                  <Checkbox
                    variant="primary"
                    verticalCentered
                    id="select-all"
                    name="select-all"
                    value="select-all"
                    onChange={handleSelectAllChange}
                    isChecked={isSelectAllChecked}
                    data-cy={dataCy ? `${dataCy}-select-all` : undefined}
                  >
                    Select all
                  </Checkbox>
                </li>
              )}
              {options.map(option => {
                const id = `checkbox__${option.id}`;
                const isChecked = state.tempSelectedOptions.has(option.id);

                return (
                  <li key={id}>
                    <Checkbox
                      verticalCentered
                      id={id}
                      name={id}
                      value={id}
                      onChange={e =>
                        handleOptionChange(option.id, e.target.checked)
                      }
                      isChecked={isChecked}
                      variant="primary"
                      dataCy={
                        dataCy ? `${dataCy}-option-${option.id}` : undefined
                      }
                    >
                      {option.label}
                    </Checkbox>
                  </li>
                );
              })}
            </ul>
            {isResetVariant && (
              <Fragment>
                <span className={c('__reset-divider')} aria-hidden />
                <button
                  type="button"
                  className={`${c(
                    '__reset-button',
                  )} focus-visible:focus-ring-form-inset`}
                  onClick={handleResetToDefault}
                  disabled={disabled || loading}
                  data-cy={dataCy ? `${dataCy}-reset-default` : undefined}
                >
                  <ArrowCounterClockwise size={18} aria-hidden />
                  <span>{resetLabel}</span>
                </button>
              </Fragment>
            )}
            {!isResetVariant && withActions && (
              <footer className={c('__footer')}>
                <button
                  type="button"
                  className={`${c(
                    '__footer-button',
                    '__footer-button--ghost',
                  )} focus-visible:focus-ring-form-inset`}
                  onClick={handleReset}
                  data-cy={dataCy ? `${dataCy}-reset` : undefined}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className={`${c(
                    '__footer-button',
                  )} focus-visible:focus-ring-form-inset`}
                  onClick={() => {
                    handleApply(handleClose);
                  }}
                  data-cy={dataCy ? `${dataCy}-apply` : undefined}
                >
                  Apply
                </button>
              </footer>
            )}
          </div>
        );
      }}
    />
  );
};

export const SelectCheckbox = memo(RawSelectCheckbox);
